import { FC, ReactNode } from 'react'
import { theme } from 'antd'
import styled from 'styled-components'
import ColorProofIndicator from '../../../../components/color-proof-indicator/color-proof-indicator'
import TableWarningIndicator from '../../../../components/table-warning-indicator/table-warning-indicator'
import { CellValidator } from '../../../../helpers/cell-validators'
import CalculationPosition from '../../../../types/api/calculation-position'
import isCalculation from '../../helpers/is-calculation'
import { DataSourceItem } from '../../helpers/convert-calculations-to-data-source'

export interface ValidatorWithLocation {
    validator: CellValidator
    location: string
}

interface ValidationError {
    location: string
    message: ReactNode
}

interface ValidationStatusCellProps {
    record: DataSourceItem
    validators: ValidatorWithLocation[]
}

const formatErrorLine = (error: ValidationError): ReactNode => (
    <>
        <strong>{error.location}</strong>: {error.message}
    </>
)

const buildErrorTooltip = (errors: ValidationError[]): ReactNode => {
    if (errors.length === 1) {
        return formatErrorLine(errors[0])
    }

    return (
        <ul style={{ margin: 0, paddingLeft: 16 }}>
            {errors.map((error, index) => (
                <li key={index}>{formatErrorLine(error)}</li>
            ))}
        </ul>
    )
}

const ValidationStatusCell: FC<ValidationStatusCellProps> = ({ record, validators }) => {
    const { token } = theme.useToken()

    if (isCalculation(record)) return null

    const cp = record as CalculationPosition

    if (cp.shipmentType === 'colorProof') {
        return (
            <Center>
                <ColorProofIndicator title='Цветопроба — проверки не применяются' />
            </Center>
        )
    }

    const errorColor = token.colorErrorText || token.colorError
    const errors = validators
        .map(({ validator, location }) => {
            const result = validator(cp, token)
            if (!result || result.color !== errorColor) return null
            return { location, message: result.message } satisfies ValidationError
        })
        .filter((error): error is ValidationError => error !== null)

    if (errors.length === 0) {
        return <TableWarningIndicator status='success' tooltip='Все проверки пройдены' />
    }

    return <TableWarningIndicator status='error' tooltip={buildErrorTooltip(errors)} />
}

const Center = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
`

export default ValidationStatusCell
